import { graphFetch } from './client';
import {
  isRetryableStatus,
  MAX_RETRIES,
  parseRetryAfter,
  nextRetryDelay,
  sleep,
} from './retry';

/**
 * Graph JSON batching (spec §3): all multi-item writes — audit submissions,
 * sync-queue drains — go through here, never one-at-a-time request loops.
 * Graph allows at most 20 sub-requests per $batch call; throttled
 * sub-requests are retried individually honoring Retry-After.
 */
export const BATCH_LIMIT = 20;

export interface BatchRequest {
  /** Caller-assigned identifier, unique within one executeBatch call. */
  id: string;
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  /** Path relative to the Graph v1.0 base, e.g. /sites/{id}/lists/Stores/items */
  url: string;
  body?: unknown;
  /** Extra sub-request headers (e.g. If-Match). */
  headers?: Record<string, string>;
}

export interface BatchResponse {
  id: string;
  status: number;
  body?: unknown;
}

interface RawBatchResponse {
  responses: Array<{
    id: string;
    status: number;
    headers?: Record<string, string>;
    body?: unknown;
  }>;
}

export function chunk<T>(items: readonly T[], size: number): T[][] {
  if (size < 1) throw new Error('chunk size must be >= 1');
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    out.push(items.slice(i, i + size));
  }
  return out;
}

/**
 * Graph returns $batch sub-response header keys in lowercase (`retry-after`),
 * but that casing is not contractual — match case-insensitively.
 */
export function headerValue(
  headers: Record<string, string> | undefined,
  name: string,
): string | null {
  if (!headers) return null;
  const lower = name.toLowerCase();
  for (const [key, value] of Object.entries(headers)) {
    if (key.toLowerCase() === lower) return value;
  }
  return null;
}

function toBatchPayload(requests: BatchRequest[]) {
  return {
    requests: requests.map((r) => ({
      id: r.id,
      method: r.method,
      url: r.url,
      ...(r.body !== undefined || r.headers
        ? {
            headers: {
              ...(r.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
              ...r.headers,
            },
          }
        : {}),
      ...(r.body !== undefined ? { body: r.body } : {}),
    })),
  };
}

/**
 * Execute requests in $batch chunks of 20. Sub-requests that come back
 * throttled (429/5xx) are re-batched and retried with backoff. Returns
 * responses keyed by request id; non-retryable failures are returned to the
 * caller rather than thrown, so callers can decide per-item (a sync queue
 * requeues, an admin screen surfaces the error).
 */
export async function executeBatch(
  requests: readonly BatchRequest[],
): Promise<Map<string, BatchResponse>> {
  const ids = new Set(requests.map((r) => r.id));
  if (ids.size !== requests.length) {
    throw new Error('executeBatch: duplicate request ids');
  }

  const results = new Map<string, BatchResponse>();
  let pending = [...requests];

  let waited = 0;
  for (let attempt = 0; pending.length > 0; attempt++) {
    const retryQueue: BatchRequest[] = [];
    let maxRetryAfter: number | undefined;

    for (const group of chunk(pending, BATCH_LIMIT)) {
      const raw = await graphFetch<RawBatchResponse>('/$batch', {
        method: 'POST',
        body: toBatchPayload(group),
      });
      for (const res of raw.responses) {
        if (isRetryableStatus(res.status) && attempt < MAX_RETRIES) {
          const original = group.find((r) => r.id === res.id);
          if (original) {
            retryQueue.push(original);
            const ra = parseRetryAfter(headerValue(res.headers, 'Retry-After'));
            if (ra !== undefined) {
              maxRetryAfter = Math.max(maxRetryAfter ?? 0, ra);
            }
            continue;
          }
        }
        results.set(res.id, { id: res.id, status: res.status, body: res.body });
      }
    }

    pending = retryQueue;
    if (pending.length > 0) {
      const delay = nextRetryDelay(attempt, maxRetryAfter, waited);
      if (delay === null) {
        // Out of time: what's still throttled is reported as busy, per item.
        for (const r of pending) results.set(r.id, { id: r.id, status: 503 });
        break;
      }
      waited += delay;
      await sleep(delay);
    }
  }

  return results;
}
